import React, { useEffect, useState, useRef } from 'react';
import Modal from '../Modal';
import TextInput from 'components/form/TextInput';
import Button from 'components/form/Button';
import { icons } from 'utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConferencesList,
  setAttendancePayload,
  setIsAttendanceConferencePopup,
  setIsAttendanceConfirmationPopup,
  throwError,
} from 'store/slices';
import { decrypt, objectToQueryParams } from 'utils/helpers';
import './AttendanceConferencePopup.scss';
import Dropdown from 'components/form/Dropdown';
import { attendanceType } from '../../../utils/constants/global';
import { Html5Qrcode } from 'html5-qrcode';

const AttendanceConferencePopup = () => {
  const dispatch = useDispatch();
  const [textInputValue, setTextInputValue] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [isScanningFailed, setIsScanningFailed] = useState(false);
  const [hasCameraPermission, setHasCameraPermission] = useState(null);
  const { conferencesList } = useSelector((state) => ({
    conferencesList: state.global.conferencesList,
  }));
  const [conferenceEvent, setConferenceEvent] = useState('');
  const [joinAs, setJoinAs] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [isScannerLoading, setIsScannerLoading] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const getConferenceList = async () => {
    await dispatch(
      fetchConferencesList(objectToQueryParams({ event_type: 'upcoming' }))
    );
  };

  const handleScan = (decodedText) => {
    if (decodedText) {
      try {
        const queryParams = new URLSearchParams(new URL(decodedText)?.search);
        const encodedEventId = queryParams.get('event_id');
        const encodedUserId = queryParams.get('user_id');
        const encodedEmailId = queryParams.get('email_id');
        const encodedRegistrationType = queryParams.get('registration_type');
        const encodedCoAuthorId = queryParams.get('co_author_id');

        // Replace spaces with + for all encoded values
        const safeEventId = encodedEventId?.replace(/ /g, '+');
        const safeUserId = encodedUserId?.replace(/ /g, '+');
        const safeEmailId = encodedEmailId?.replace(/ /g, '+');
        const safeRegistrationType = encodedRegistrationType?.replace(
          / /g,
          '+'
        );
        const safeCoAuthorId = encodedCoAuthorId?.replace(/ /g, '+');

        const event_id = safeEventId ? decrypt(safeEventId) : false;
        const user_id = safeUserId ? decrypt(safeUserId) : false;
        const email_id = safeEmailId ? decrypt(safeEmailId) : false;
        const registration_type = safeRegistrationType
          ? decrypt(safeRegistrationType)
          : false;
        const co_author_id = safeCoAuthorId ? decrypt(safeCoAuthorId) : false;
        if (event_id) {
          dispatch(
            setAttendancePayload({
              event_id: event_id,
              user_id: user_id,
              attendance_type: 1,
              email_id: email_id,
              registration_type: registration_type,
              ...(co_author_id ? { co_author_id } : {})
            })
          );
          setShowScanner(false);
          dispatch(setIsAttendanceConferencePopup(false));
          dispatch(setIsAttendanceConfirmationPopup(true));
        } else {
          dispatch(throwError({ message: 'Please scan valid QR' }));
          setShowScanner(false);
        }
      } catch (err) {
        dispatch(throwError({ message: 'Invalid QR Code' }));
        setShowScanner(false);
      }
    }
  };

  const checkCameraPermission = async () => {
    try {
      const stream = await navigator?.mediaDevices?.getUserMedia({
        video: true,
      });
      stream?.getTracks()?.forEach((track) => track?.stop());
      setHasCameraPermission(true);
      return true;
    } catch (err) {
      console.error('Camera access error:', err);
      setHasCameraPermission(false);
      return false;
    }
  };


  useEffect(() => {
    if (navigator?.mediaDevices && navigator?.mediaDevices?.getUserMedia) {
      checkCameraPermission();
    } else {
      setHasCameraPermission(false);
    }
  }, []);

  useEffect(() => {
    getConferenceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch cameras on mount with error handling
  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const devices = await Html5Qrcode.getCameras();
        // console.log('✌️devices --->', devices);
        if (devices.length > 0) {
          setCameras(devices);
          setSelectedCameraId(devices[0].id);
        } else {
          setHasCameraPermission(false);
        }
      } catch (error) {
        console.error('Error fetching cameras:', error);
        setHasCameraPermission(false);
      }
    };

    fetchCameras();
  }, []);

  // Start/stop scanner with comprehensive error handling
  useEffect(() => {
    let isMounted = true;
    let stopping = false;

    async function cleanupScanner() {
      if (html5QrCodeRef.current) {
        try {
          if (!stopping) {
            stopping = true;
            await html5QrCodeRef.current.stop();
          }
          await html5QrCodeRef.current.clear();
        } catch (e) {
          console.error('Scanner cleanup error:', e);
        }
        html5QrCodeRef.current = null;
      }
      setIsScannerLoading(false);
    }

    const startScanner = async () => {
      if (
        showScanner &&
        selectedCameraId &&
        scannerRef.current &&
        !isScannerLoading &&
        hasCameraPermission
      ) {
        setIsScannerLoading(true);
        try {
          html5QrCodeRef.current = new Html5Qrcode(scannerRef.current.id);
          await html5QrCodeRef.current.start(
            { deviceId: { exact: selectedCameraId } },
            {
              fps: 10,
              qrbox: { width: 250, height: 250 },
            },
            (decodedText) => {
              if (isMounted) {
                handleScan(decodedText);
                cleanupScanner();
              }
            },
            (errorMessage) => {
              console.error('QR scan error:', errorMessage);
            }
          );
          if (isMounted) setIsScannerLoading(false);
        } catch (error) {
          console.error('Scanner start error:', error);
          if (isMounted) {
            setIsScanningFailed(true);
            setShowScanner(false);
            setIsScannerLoading(false);
          }
        }
      }
    };

    startScanner();

    return () => {
      isMounted = false;
      cleanupScanner();
    };
  }, [showScanner, selectedCameraId, hasCameraPermission]);

  return (
    <Modal
      onHide={() => {
        dispatch(setIsAttendanceConferencePopup(false));
      }}
      size="md"
      title={
        showScanner ? (
          'Scan QR Code'
        ) : isScanningFailed ? (
          <img src={icons?.circleInfo} alt="info" />
        ) : (
          'Conference Attendance'
        )
      }
    >
      {showScanner ? (
        <>
          <div className="d-flex align-items-center justify-content-center">
            <p className="text-16-300-20 color-black-olive">
              Please QR Code Inside the frame
            </p>
          </div>
          <div className="qr-scanner-container">
            <div
              id="qr-reader"
              ref={scannerRef}
              className="qr-reader"
              style={{ width: '100%', height: '100%' }}
            />
            <div className="text-center text-16-300-20 color-black-olive">
              Scanning the QR Code...
            </div>
            {cameras.length > 1 && (
              <Button
                btnStyle="primary-outline"
                text={`Switch to ${selectedCameraId === cameras[0].id ? 'Back' : 'Front'
                  } Camera`}
                onClick={async () => {
                  if (isScannerLoading) return;
                  setIsScannerLoading(true);
                  setIsScanningFailed(false);
                  // Stop current scanner before switching
                  if (html5QrCodeRef.current) {
                    try {
                      await html5QrCodeRef.current.stop();
                      await html5QrCodeRef.current.clear();
                    } catch (e) {
                      console.error('Error switching cameras:', e);
                    }
                  }
                  setSelectedCameraId((prev) =>
                    prev === cameras[0].id ? cameras[1].id : cameras[0].id
                  );
                  setIsScannerLoading(false);
                }}
                className="h-35 cmb-10 cmt-10"
                disabled={isScannerLoading}
              />
            )}
            <Button
              btnStyle={'unset-primary'}
              text="Cancel"
              onClick={() => setShowScanner(false)}
            />
          </div>
        </>
      ) : (
        <>
          <div className="attendance-conference-popup-container d-flex flex-column align-items-center cps-25 cpe-25">
            <p className="text-16-300-20 color-black-olive cmt-20 cmb-20">
              {isScanningFailed
                ? 'Unable to verify the QR Code'
                : 'Please choose any one option to mark the attendance'}
            </p>
            {hasCameraPermission === false ? (
              <div className="text-14-300-20 color-red cmb-20">
                Camera access is not available. Please check your device
                permissions or connect a camera.
              </div>
            ) : (
              <div
                className={`cmb-23 d-flex align-items-center gap-2 scan-button pointer${isScannerLoading ? ' disabled' : ''
                  }`}
                onClick={async () => {
                  if (isScannerLoading) return;

                  // Check camera permission if not already determined
                  if (hasCameraPermission === null) {
                    const hasPermission = await checkCameraPermission();
                    if (!hasPermission) {
                      setIsScanningFailed(true);
                      return;
                    }
                  }

                  if (hasCameraPermission === true) {
                    setShowScanner(true);
                  } else {
                    setIsScanningFailed(true);
                  }
                }}
                style={
                  isScannerLoading
                    ? { pointerEvents: 'none', opacity: 0.6 }
                    : {}
                }
              >
                <img src={icons?.primaryScanner} alt="scanner" />
                <span className="text-20-400-25 color-new-car">{`Scan QR ${isScanningFailed ? 'Again' : ''
                  }`}</span>
              </div>
            )}
            <div className="cmb-23">Or</div>
          </div>
          <div className="cmb-20 cps-25 cpe-25">
            <Dropdown
              id="id"
              label="Event"
              placeholder="Select Event"
              value={conferenceEvent}
              options={conferencesList}
              optionValue="event_name"
              onChange={(e) => setConferenceEvent(e.target.value)}
            />
          </div>
          <div className="cmb-20 cps-25 cpe-25">
            <TextInput
              label="Phone number/ Membership id/ Paper id/ Email id"
              placeholder="Enter Phone No."
              value={textInputValue}
              id={'textInputValue'}
              onChange={(e) => {
                setTextInputValue(e.target.value);
              }}
            />
          </div>
          <div className="cmb-20 cps-25 cpe-25">
            <Dropdown
              id="join_as"
              label="Join As"
              placeholder="Select Join As"
              value={joinAs}
              options={attendanceType}
              onChange={(e) => setJoinAs(e.target.value)}
              optionKey="id"
              optionValue="label"
            />
          </div>

          <div className="cps-25 cpe-25 cmb-30">
            <Button
              btnStyle={'primary-dark'}
              text="Mark Attendance"
              onClick={() => {
                dispatch(
                  setAttendancePayload({
                    event_id: conferenceEvent,
                    common_input_value: textInputValue,
                    attendance_type: 2,
                    registration_type: joinAs,
                  })
                );
                dispatch(setIsAttendanceConfirmationPopup(true));
                dispatch(setIsAttendanceConferencePopup(false));
              }}
              disabled={!textInputValue || !conferenceEvent || !joinAs}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default AttendanceConferencePopup;

// import React, { useEffect, useState } from 'react';
// import Modal from '../Modal';
// import TextInput from 'components/form/TextInput';
// import Button from 'components/form/Button';
// import { icons } from 'utils/constants';
// import { useDispatch, useSelector } from 'react-redux';
// import {
//   fetchConferencesList,
//   setAttendancePayload,
//   setIsAttendanceConferencePopup,
//   setIsAttendanceConfirmationPopup,
//   throwError,
// } from 'store/slices';
// import { QrReader } from 'react-qr-reader';
// import { decrypt, objectToQueryParams } from 'utils/helpers';
// import './AttendanceConferencePopup.scss';
// import Dropdown from 'components/form/Dropdown';
// import { attendanceType } from '../../../utils/constants/global';

// const AttendanceConferencePopup = () => {
//   const dispatch = useDispatch();
//   const [textInputValue, setTextInputValue] = useState('');
//   const [showScanner, setShowScanner] = useState(false);
//   const [isScanningFailed, setIsScanningFailed] = useState(false);
//   const [hasCameraPermission, setHasCameraPermission] = useState(null);
//   const { conferencesList } = useSelector((state) => ({
//     conferencesList: state.global.conferencesList,
//   }));
//   const [conferenceEvent, setConferenceEvent] = useState('');
//   const [joinAs, setJoinAs] = useState('');
//   const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back

//   const getConferenceList = async () => {
//     await dispatch(
//       fetchConferencesList(objectToQueryParams({ event_type: 'upcoming' }))
//     );
//   };

//   const handleScan = (data) => {
//     if (data) {
//       setShowScanner(false);
//       setIsScanningFailed(false);
//     }
//   };

//   const handleError = (err) => {
//     setIsScanningFailed(true);
//     setShowScanner(false);
//   };
//   const isMobile = /Mobi|Android/i.test(navigator.userAgent);

//   const toggleFacingMode = () => {
//     setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
//   };

//   const checkCameraPermission = async () => {
//     try {
//       const stream = await navigator?.mediaDevices?.getUserMedia({
//         video: true,
//       });
//       stream?.getTracks()?.forEach((track) => track?.stop());
//       setHasCameraPermission(true);
//     } catch (err) {
//       setHasCameraPermission(false);
//     }
//   };

//   useEffect(() => {
//     if (navigator?.mediaDevices && navigator?.mediaDevices?.getUserMedia) {
//       checkCameraPermission();
//     } else {
//       setHasCameraPermission(false);
//     }
//   }, []);

//   useEffect(() => {
//     getConferenceList();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const constraints = {
//     video: {
//       facingMode: isMobile ? facingMode : 'user',
//       // width: { ideal: 1920 },
//       // height: { ideal: 1080 },
//       width: { ideal: 1280 },
//       height: { ideal: 720 },
//     },
//   };

//   return (
//     <Modal
//       onHide={() => {
//         dispatch(setIsAttendanceConferencePopup(false));
//       }}
//       size="md"
//       title={
//         showScanner ? (
//           'Scan QR Code'
//         ) : isScanningFailed ? (
//           <img src={icons?.circleInfo} alt="info" />
//         ) : (
//           'Conference Attendance'
//         )
//       }
//     >
//       {showScanner ? (
//         <>
//           <div className="d-flex align-items-center justify-content-center">
//             <p className="text-16-300-20 color-black-olive">
//               Please QR Code Inside the frame
//             </p>
//           </div>
//           <div className="qr-scanner-container">
//             <QrReader
//               key={facingMode}
//               delay={500}
//               onError={handleError}
//               onScan={handleScan}
//               constraints={constraints}
//               onResult={(result, error) => {
//                 if (result) {
//                   // "http://localhost:3000/member/login?event_id=Yr2aKedr21zA2yqOLE9fZA==&user_id=zruxbi9U29GS+L2OqJ1S2A==";
//                   const queryParams = new URLSearchParams(
//                     new URL(result?.text)?.search
//                   );
//                   const encodedEventId = queryParams.get('event_id');
//                   const encodedUserId = queryParams.get('user_id');
//                   const encodedEmailId = queryParams.get('email_id');
//                   const encodedRegistrationType =
//                     queryParams.get('registration_type');
//                   const formattedUserId = encodedUserId?.includes(' ')
//                     ? encodedUserId?.replace(/ /g, '+')
//                     : encodedUserId;

//                   const event_id = encodedEventId
//                     ? decrypt(encodedEventId)
//                     : false;
//                   const user_id = encodedUserId
//                     ? decrypt(formattedUserId)
//                     : false;
//                   const email_id = encodedEmailId
//                     ? decrypt(encodedEmailId)
//                     : false;
//                   const registration_type = encodedRegistrationType
//                     ? decrypt(encodedRegistrationType)
//                     : false;
//                   // Redirect to the scanned URL
//                   if (event_id && user_id) {
//                     dispatch(
//                       setAttendancePayload({
//                         event_id: event_id,
//                         user_id: user_id,
//                         attendance_type: 1,
//                         email_id: email_id,
//                         registration_type: registration_type,
//                       })
//                     );
//                     setShowScanner(false);
//                     dispatch(setIsAttendanceConferencePopup(false));
//                     dispatch(setIsAttendanceConfirmationPopup(true));
//                   } else {
//                     dispatch(throwError({ message: 'Please scan valid QR' }));
//                     setShowScanner(false);
//                   }
//                 }

//                 if (error) {
//                   console.log('ee', error);
//                 }
//               }}
//               style={{ width: '100%', height: '300px' }}
//             />
//             <div className="text-center text-16-300-20 color-black-olive">
//               Scanning the QR Code...
//             </div>
//             {isMobile && (
//               <Button
//                 onClick={toggleFacingMode}
//                 btnStyle="unset-primary"
//                 text={`Switch to
//                 ${facingMode === 'user' ? 'Back Camera' : 'Front Camera'}`}
//               />
//             )}
//             <Button
//               btnStyle={'unset-primary'}
//               text="Cancel"
//               onClick={() => setShowScanner(false)}
//             />
//           </div>
//         </>
//       ) : (
//         <>
//           <div className="attendance-conference-popup-container d-flex flex-column align-items-center cps-25 cpe-25">
//             <p className="text-16-300-20 color-black-olive cmt-20 cmb-40">
//               {isScanningFailed
//                 ? 'Unable to verify the QR Code'
//                 : 'Please choose any one option to mark the attendance'}
//             </p>
//             <div
//               className="cmb-23 d-flex align-items-center gap-2 scan-button pointer"
//               onClick={() => {
//                 if (hasCameraPermission === true) {
//                   setShowScanner(true);
//                 } else {
//                   setIsScanningFailed(true); // Set error state if permission is not granted
//                 }
//               }}
//             >
//               <img src={icons?.primaryScanner} alt="scanner" />
//               <span className="text-20-400-25 color-new-car">{`Scan QR ${
//                 isScanningFailed ? 'Again' : ''
//               }`}</span>
//             </div>
//             <div className="cmb-23">Or</div>
//           </div>
//           <div className="cmb-20 cps-25 cpe-25">
//             <Dropdown
//               id="id"
//               label="Event"
//               placeholder="Select Event"
//               value={conferenceEvent}
//               options={conferencesList}
//               optionValue="event_name"
//               onChange={(e) => setConferenceEvent(e.target.value)}
//             />
//           </div>
//           <div className="cmb-20 cps-25 cpe-25">
//             <TextInput
//               label="Phone number/ Membership id/ Paper id/ Email id"
//               placeholder="Enter Phone No."
//               value={textInputValue}
//               id={'textInputValue'}
//               onChange={(e) => {
//                 setTextInputValue(e.target.value);
//               }}
//             />
//           </div>
//           <div className="cmb-20 cps-25 cpe-25">
//             <Dropdown
//               id="join_as"
//               label="Join As"
//               placeholder="Select Join As"
//               value={joinAs}
//               options={attendanceType}
//               onChange={(e) => setJoinAs(e.target.value)}
//               optionKey="id"
//               optionValue="label"
//             />
//           </div>

//           <div className="cps-25 cpe-25 cmb-30">
//             <Button
//               btnStyle={'primary-dark'}
//               text="Mark Attendance"
//               onClick={() => {
//                 dispatch(
//                   setAttendancePayload({
//                     event_id: conferenceEvent,
//                     common_input_value: textInputValue,
//                     attendance_type: 2,
//                     registration_type: joinAs,
//                   })
//                 );
//                 dispatch(setIsAttendanceConfirmationPopup(true));
//                 dispatch(setIsAttendanceConferencePopup(false));
//               }}
//               disabled={!textInputValue || !conferenceEvent || !joinAs}
//             />
//           </div>
//         </>
//       )}
//     </Modal>
//   );
// };

// export default AttendanceConferencePopup;
